/**
 * Storage Service
 *
 * Supabase Storage integration for file upload, download, and management.
 *
 * Mode behavior:
 * - MOCK MODE: Simulates upload with fake URLs (no network calls)
 * - DEMO MODE: Real uploads to demo Supabase bucket
 * - PRODUCTION MODE: Real uploads to production bucket
 */

import { supabase } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { File as ExpoFile } from 'expo-file-system';
import { Platform } from 'react-native';
import type {
  StorageFile,
  UploadOptions,
  UploadResult,
  StorageBucket,
  FilePickResult,
} from './types';
import { STORAGE_BUCKETS } from './types';
import { validateFile } from './config';

/**
 * Upload a file to Supabase Storage
 */
export async function uploadFile(
  file: FilePickResult,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const bucket = (options.bucket || 'dataroom') as StorageBucket;

  // Validate file
  const validation = validateFile({ size: file.size, mimeType: file.mimeType }, bucket);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  // MOCK mode - simulate upload (no network calls)
  if (mode.isMock) {
    return simulateUpload(file, options);
  }

  try {
    // Generate unique path
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const path = options.path ? `${options.path}/${timestamp}-${safeName}` : `${timestamp}-${safeName}`;

    // Read file data
    let fileData: ArrayBuffer | Blob;
    if (Platform.OS === 'web') {
      const response = await fetch(file.uri);
      fileData = await response.blob();
    } else {
      // Use the new expo-file-system File API
      const expoFile = new ExpoFile(file.uri);
      fileData = await expoFile.arrayBuffer();
    }

    // Upload to Supabase
    const { data, error } = await supabase.storage.from(bucket).upload(path, fileData, {
      contentType: file.mimeType || 'application/octet-stream',
      upsert: options.upsert ?? false,
      cacheControl: options.cacheControl || '3600',
    });

    if (error) {
      return { success: false, error: error.message };
    }

    // Get public URL if bucket is public
    let publicUrl: string | undefined;
    if (STORAGE_BUCKETS[bucket]?.public) {
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
      publicUrl = urlData.publicUrl;
    }

    const storageFile: StorageFile = {
      id: data.id || path,
      name: file.name,
      size: file.size || 0,
      mimeType: file.mimeType || 'application/octet-stream',
      bucket,
      path: data.path,
      publicUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: options.metadata,
    };

    return { success: true, file: storageFile, publicUrl };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
}

/**
 * Delete a file from Supabase Storage
 */
export async function deleteFile(
  bucket: StorageBucket,
  path: string
): Promise<{ success: boolean; error?: string }> {
  if (mode.isMock) {
    return { success: true };
  }

  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed',
    };
  }
}

/**
 * Get a signed URL for private bucket files
 */
export async function getSignedUrl(
  bucket: StorageBucket,
  path: string,
  expiresIn: number = 3600
): Promise<{ url?: string; error?: string }> {
  if (mode.isMock) {
    return { url: `https://demo.storage/${bucket}/${path}` };
  }

  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);

    if (error) {
      return { error: error.message };
    }

    return { url: data.signedUrl };
  } catch (error) {
    return {
      error: error instanceof Error ? error.message : 'Failed to get URL',
    };
  }
}

/**
 * List files in a bucket path
 */
export async function listFiles(
  bucket: StorageBucket,
  path: string = ''
): Promise<{ files: StorageFile[]; error?: string }> {
  if (mode.isMock) {
    return { files: [] };
  }

  try {
    const { data, error } = await supabase.storage.from(bucket).list(path);

    if (error) {
      return { files: [], error: error.message };
    }

    const files: StorageFile[] = data
      .filter((item) => item.id) // Exclude folders
      .map((item) => ({
        id: item.id!,
        name: item.name,
        size: item.metadata?.size || 0,
        mimeType: item.metadata?.mimetype || 'application/octet-stream',
        bucket,
        path: path ? `${path}/${item.name}` : item.name,
        createdAt: item.created_at || new Date().toISOString(),
        updatedAt: item.updated_at || new Date().toISOString(),
      }));

    return { files };
  } catch (error) {
    return {
      files: [],
      error: error instanceof Error ? error.message : 'List failed',
    };
  }
}

/**
 * Simulate file upload for demo mode
 */
async function simulateUpload(file: FilePickResult, options: UploadOptions): Promise<UploadResult> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Simulate progress if callback provided
  if (options.onProgress) {
    const total = file.size || 1000000;
    for (let i = 0; i <= 100; i += 10) {
      options.onProgress({
        loaded: Math.round((total * i) / 100),
        total,
        percentage: i,
      });
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  const bucket = options.bucket || 'dataroom';
  const timestamp = Date.now();
  const path = options.path ? `${options.path}/${timestamp}-${file.name}` : `${timestamp}-${file.name}`;

  const storageFile: StorageFile = {
    id: `demo-${timestamp}`,
    name: file.name,
    size: file.size || 0,
    mimeType: file.mimeType || 'application/octet-stream',
    bucket,
    path,
    publicUrl: `https://demo.storage/${bucket}/${path}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metadata: options.metadata,
  };

  return {
    success: true,
    file: storageFile,
    publicUrl: storageFile.publicUrl,
  };
}
